import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Heart,
  Share2,
  MapPin,
  Clock,
  Star,
  Navigation,
  Car,
  Bus,
  Accessibility,
  Sun,
  Users,
  Info,
  Calendar,
  DogIcon,
  Baby,
  ExternalLink,
  Play,
  ChevronLeft,
  ChevronRight,
  X,
  Grid3X3,
  CheckCircle
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import AddToListDropdown from '../components/AddToListDropdown';
import SEOHead from '../components/SEOHead';
import WalkRouteMap from '../components/WalkRouteMap';
import WalkHighlights from '../components/WalkHighlights';
import POIDetailModal from '../components/POIDetailModal';
import WalkCard from '../components/WalkCard';
import ShareModal from '../components/ShareModal';
import WalkFeedbackModal from '../components/WalkFeedbackModal';
import WeatherWidget from '../components/WeatherWidget';
import RouteTimeline, { RouteTimelineIndex } from '../components/RouteTimeline';
import { isFavorite as isLocalFavorite, addWalkToList, removeWalkFromList } from '../data/mock';
import { useAuth } from '../contexts/AuthContext';
import { getFreshToken } from '../lib/firebase';
import { generateWalkSchema, generateBreadcrumbSchema, getWalkKeywords } from '../utils/seoData';
import formatDuration from '../utils/formatDuration';
import categoryLabel, { categoryColor, difficultyColor, distanceCategory } from '../utils/categoryLabels';
import walkUrl from '../utils/walkUrl';

const transformWalk = (w) => {
  const acc = w.accessibility || {};
  const distanceMiles = w.distance_meters
    ? (w.distance_meters / 1609.34).toFixed(1)
    : null;
  const photos = [w.main_image, ...(w.images || [])]
    .filter(Boolean)
    .filter((v, i, a) => a.indexOf(v) === i); // dedupe

  return {
    id: w.id,
    slug: w.slug,
    country: w.country || 'us',
    city: w.city || 'los-angeles',
    neighborhood: w.neighborhood || '',
    title: w.title,
    description: w.description,
    practicalSummary: w.practical_summary || w.description,
    startingPoint: w.starting_point,
    endingPoint: w.ending_point,
    startingAddress: w.starting_address || w.starting_point,
    endingAddress: w.ending_address || w.ending_point,
    startingCoords: w.starting_coords || { lat: 0, lng: 0 },
    endingCoords: w.ending_coords || { lat: 0, lng: 0 },
    routeCoordinates: w.route_coordinates || [],
    distance: distanceMiles ? `${distanceMiles} miles` : null,
    duration: formatDuration(w.duration_minutes),
    difficulty: w.difficulty,
    walkShape: w.walk_shape || 'loop',
    safetyLevel: w.safety_level || 'Safe Anytime',
    distanceCategory: w.distance_category || distanceCategory(w.distance_meters),
    category: categoryLabel(w.categories?.[0]) || 'Urban Walk',
    categories: w.categories || [],
    region: w.region,
    photos: photos.length > 0 ? photos : [],
    highlights: w.highlights || [],
    culturalPois: w.cultural_pois || [],
    waypoints: w.waypoints || [],
    relatedGearIds: w.related_gear_ids || [],
    featured: w.featured || false,
    userRatings: w.user_ratings || 0,
    ratingCount: w.rating_count || 0,
    favoriteCount: w.favorite_count || 0,
    completionCount: w.completion_count || 0,
    bestTimes: Array.isArray(w.best_times) ? w.best_times : (w.best_times ? [w.best_times] : []),
    gearNotes: w.gear_notes || '',
    gearEditorialNotes: w.gear_editorial_notes || {},
    funCategories: w.fun_categories ? {
      themeSong: w.fun_categories.theme_song || '',
      vibeIn3Words: (() => {
        const v = w.fun_categories.vibe_in_3_words;
        if (Array.isArray(v)) return v.join(', ');
        return v || '';
      })(),
      instagramMoment: w.fun_categories.instagram_moment || '',
      perfectSnack: w.fun_categories.perfect_snack || '',
      walkingChallenge: w.fun_categories.walking_challenge || '',
      movieThatMatches: w.fun_categories.movie_that_matches || '',
    } : null,
    unsplashAttributions: w._unsplash_attributions || (w._unsplash_attribution?.hero ? [w._unsplash_attribution.hero] : []),
    accessibility: {
      strollerFriendly: acc.stroller_friendly ?? acc.strollerFriendly ?? null,
      wheelchairAccessible: acc.wheelchair_accessible ?? acc.wheelchairAccessible ?? null,
      parkingAvailable: acc.parking_available ?? acc.parkingAvailable ?? null,
      restrooms: acc.restrooms ?? null,
      shadeCoverage: acc.shade_coverage ?? acc.shadeCoverage ?? null,
      dogFriendly: acc.dog_friendly ?? acc.dogFriendly ?? null,
    },
    seoTags: (w.seo_tags || []).flatMap(t => t.includes(',') ? t.split(',').map(s => s.trim()).filter(Boolean) : [t]),
    metaTitle: w.meta_title || w.title,
    metaDescription: w.meta_description || w.practical_summary || w.description,
  };
};

const WalkDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isTimeline = new URLSearchParams(window.location.search).get('layout') === 'timeline';
  const { isAuthenticated } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const [walk, setWalk] = useState(null);
  const [relatedWalks, setRelatedWalks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favorite, setFavorite] = useState(false);
  const [selectedPOI, setSelectedPOI] = useState(null);
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  const [showPhotoSlideshow, setShowPhotoSlideshow] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [heroPhotoIndex, setHeroPhotoIndex] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [linkedGear, setLinkedGear] = useState([]);
  const [userCompleted, setUserCompleted] = useState(false);
  const [completionCount, setCompletionCount] = useState(0);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionDate, setCompletionDate] = useState(new Date().toISOString().split('T')[0]);
  const [completionNote, setCompletionNote] = useState('');
  const [completionSubmitting, setCompletionSubmitting] = useState(false);

  useEffect(() => {
    const fetchWalk = async () => {
      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

        const response = await fetch(`${backendUrl}/api/walks/${slug}`);
        if (!response.ok) {
          setError('Walk not found');
          return;
        }
        const data = await response.json();
        console.log('[WalkDetailPage] raw API data — waypoints:', JSON.stringify(data.waypoints), '| starting_coords:', JSON.stringify(data.starting_coords), '| ending_coords:', JSON.stringify(data.ending_coords));
        const transformed = transformWalk(data);
        console.log('[WalkDetailPage] transformed — waypoints:', transformed.waypoints.length, '| startingCoords:', JSON.stringify(transformed.startingCoords));
        setWalk(transformed);
        // Check if walk is favorited — try backend first, fall back to localStorage
        try {
          const token = await getFreshToken();
          if (token) {
            const favResp = await fetch(`${backendUrl}/api/favorites`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (favResp.ok) {
              const favData = await favResp.json();
              setFavorite((favData.walk_ids || []).includes(transformed.id));
            }
          } else {
            setFavorite(isLocalFavorite(transformed.id));
          }
        } catch {
          setFavorite(isLocalFavorite(transformed.id));
        }

        // Fetch completion status
        try {
          const token = await getFreshToken();
          const compHeaders = token ? { Authorization: `Bearer ${token}` } : {};
          const compResp = await fetch(`${backendUrl}/api/walks/${transformed.id}/completions`, { headers: compHeaders });
          if (compResp.ok) {
            const compData = await compResp.json();
            setCompletionCount(compData.count || 0);
            setUserCompleted(compData.user_completed || false);
          }
        } catch { /* ignore */ }

        // Fetch related walks — same category, exclude current
        const listResponse = await fetch(`${backendUrl}/api/walks?limit=20`);
        if (listResponse.ok) {
          const listData = await listResponse.json();
          const others = (listData.walks || [])
            .filter(w => w.id !== data.id)
            .map(w => {
              const t = transformWalk(w);
              let score = 0;
              // Score by shared categories
              const tCats = w.categories || [];
              const myCats = data.categories || [];
              tCats.forEach(c => { if (myCats.includes(c)) score += 5; });
              // Score by shared tags
              const tTags = w.tags || [];
              const myTags = data.tags || [];
              tTags.forEach(tg => { if (myTags.includes(tg)) score += 3; });
              // Bonus for same difficulty
              if (t.difficulty === transformed.difficulty) score += 2;
              return { ...t, score };
            })
            .filter(w => w.score > 0) // only show walks with at least one match
            .sort((a, b) => b.score - a.score)
            .slice(0, 3);
          setRelatedWalks(others);
        }
      } catch (err) {
        console.error('Error fetching walk:', err);
        setError('Failed to load walk');
      } finally {
        setLoading(false);
      }
    };

    if (slug) fetchWalk();
  }, [slug]);

  // Track Unsplash downloads per API guidelines (once per page view)
  useEffect(() => {
    if (!walk?.unsplashAttributions?.length) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    walk.unsplashAttributions.forEach(attr => {
      if (attr?.source === 'unsplash' && attr?.download_link) {
        fetch(`${backendUrl}/api/unsplash/track-download`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ download_link: attr.download_link }),
        }).catch(() => {}); // silent fail
      }
    });
  }, [walk?.id]);

  // Fetch linked gear items
  useEffect(() => {
    if (!walk?.relatedGearIds?.length) { setLinkedGear([]); return; }
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    Promise.all(
      walk.relatedGearIds.map(gearSlug =>
        fetch(`${backendUrl}/api/gear/${gearSlug}`)
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      )
    ).then(results => setLinkedGear(results.filter(Boolean)));
  }, [walk?.relatedGearIds]);

  // Navigate to walks directory filtered by tag
  const handleTagClick = (tag) => {
    const slug = tag.replace(/^#/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '');
    const city = walk?.city || 'los-angeles';
    const country = walk?.country || 'us';
    navigate(`/walks/${country}/${city}?tag=${encodeURIComponent(slug)}`);
  };

  // Photo gallery handlers
  const openPhotoGallery = () => {
    setShowPhotoGallery(true);
  };

  const openPhotoSlideshow = (index = 0) => {
    setCurrentPhotoIndex(index);
    setShowPhotoSlideshow(true);
  };

  const nextPhoto = () => {
    if (walk && walk.photos && walk.photos.length > 0) {
      setCurrentPhotoIndex((prev) => (prev + 1) % walk.photos.length);
    }
  };

  const prevPhoto = () => {
    if (walk && walk.photos && walk.photos.length > 0) {
      setCurrentPhotoIndex((prev) => (prev - 1 + walk.photos.length) % walk.photos.length);
    }
  };

  // Keyboard navigation for fullscreen carousel
  useEffect(() => {
    if (!showPhotoSlideshow) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') nextPhoto();
      else if (e.key === 'ArrowLeft') prevPhoto();
      else if (e.key === 'Escape') setShowPhotoSlideshow(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [showPhotoSlideshow, walk]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <SEOHead 
          title="Loading Walk..."
          noindex={true}
        />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold mb-4">Loading Walk...</h2>
          <p className="text-gray-600">Please wait while we fetch the walk details.</p>
        </div>
      </div>
    );
  }

  if (error || !walk) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <SEOHead 
          title="Walk Not Found"
          noindex={true}
        />
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Walk Not Found</h2>
          <p className="text-gray-600 mb-6">The walk you're looking for doesn't exist.</p>
          <Link to="/walks">
            <Button>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Walks
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleFavoriteClick = async () => {
    const newState = !favorite;
    setFavorite(newState); // optimistic update

    try {
      const token = await getFreshToken();
      if (token) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const resp = await fetch(`${backendUrl}/api/favorites/${walk.id}`, {
          method: newState ? 'POST' : 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      }
      // Always sync to localStorage so favorites page picks it up
      if (newState) addWalkToList(walk.id, 'favorites');
      else removeWalkFromList(walk.id, 'favorites');
    } catch (e) {
      console.error('Favorite toggle failed:', e);
      setFavorite(!newState); // revert on error
    }
    setRefreshKey(prev => prev + 1);
  };

  const handleListChange = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleCompletionSubmit = async () => {
    setCompletionSubmitting(true);
    try {
      const token = await getFreshToken();
      if (!token) return;
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const resp = await fetch(`${backendUrl}/api/walks/${walk.id}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ date: completionDate, note: completionNote }),
      });
      if (resp.ok) {
        setUserCompleted(true);
        setCompletionCount(prev => prev + 1);
        setShowCompletionModal(false);
        setCompletionNote('');
      }
    } catch (e) {
      console.error('Completion failed:', e);
    } finally {
      setCompletionSubmitting(false);
    }
  };

  const handleUncomplete = async () => {
    try {
      const token = await getFreshToken();
      if (!token) return;
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      await fetch(`${backendUrl}/api/walks/${walk.id}/complete`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserCompleted(false);
      setCompletionCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error('Uncomplete failed:', e);
    }
  };

  // Generate SEO data
  const keywords = getWalkKeywords(walk);
  const thisWalkUrl = walkUrl(walk);
  const countryLabel = (walk.country || 'us').toUpperCase();
  const cityLabel = (walk.city || 'los-angeles').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const neighborhoodLabel = (walk.neighborhood || walk.region || 'Explore').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const structuredData = [
    ...generateWalkSchema(walk),
    generateBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Walks', url: '/walks' },
      { name: countryLabel, url: `/walks/${walk.country || 'us'}` },
      { name: cityLabel, url: `/walks/${walk.country || 'us'}/${walk.city || 'los-angeles'}` },
      { name: neighborhoodLabel, url: `/walks/${walk.country || 'us'}/${walk.city || 'los-angeles'}/${walk.neighborhood || walk.city || 'los-angeles'}` },
      { name: walk.title, url: thisWalkUrl }
    ])
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={walk.title}
        description={walk.practicalSummary}
        keywords={keywords}
        image={walk.photos[0]}
        url={thisWalkUrl}
        type="article"
        structuredData={structuredData}
      />
      <Header />
      
      {/* Breadcrumbs */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <ol className="flex items-center space-x-2 text-sm flex-wrap">
            <li><Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link></li>
            <li className="text-gray-400">/</li>
            <li><Link to="/walks" className="text-blue-600 hover:text-blue-800">Walks</Link></li>
            <li className="text-gray-400">/</li>
            <li><Link to={`/walks/${walk.country || 'us'}`} className="text-blue-600 hover:text-blue-800">{countryLabel}</Link></li>
            <li className="text-gray-400">/</li>
            <li><Link to={`/walks/${walk.country || 'us'}/${walk.city || 'los-angeles'}`} className="text-blue-600 hover:text-blue-800">{cityLabel}</Link></li>
            <li className="text-gray-400">/</li>
            <li><Link to={`/walks/${walk.country || 'us'}/${walk.city || 'los-angeles'}/${walk.neighborhood || walk.city || 'los-angeles'}`} className="text-blue-600 hover:text-blue-800">{neighborhoodLabel}</Link></li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-600 font-medium">{walk.title}</li>
          </ol>
        </div>
      </nav>

      {/* AllTrails-Style Hero Section */}
      <section className="relative bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 min-h-[500px]">
            
            {/* Hero Image - Left Side */}
            <div className="lg:col-span-2 relative">
              <div
                className="h-[500px] relative overflow-hidden cursor-pointer group"
                onClick={() => openPhotoSlideshow(heroPhotoIndex)}
              >
                <img
                  src={walk.photos[heroPhotoIndex] || walk.photos[0]}
                  alt={walk.unsplashAttributions?.[heroPhotoIndex]?.alt_text || `${walk.title} - Photo ${heroPhotoIndex + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />

                {/* Photo overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20"></div>

                {/* Photo attribution — updates with current photo */}
                {(() => {
                  const attr = walk.unsplashAttributions?.[heroPhotoIndex];
                  const src = attr?.source || ((walk.photos[heroPhotoIndex] || '').includes('unsplash.com') ? 'unsplash' : 'manual');
                  if (attr?.photographer) {
                    return (
                      <div className="absolute bottom-1 left-2 z-20 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
                        <span className="text-[7px] text-white/80 bg-black/40 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5">
                          {src === 'wikimedia' ? 'Image' : 'Photo'} by{' '}
                          <a href={`${attr.url}${src === 'unsplash' ? '?utm_source=wanderloop&utm_medium=referral' : ''}`} target="_blank" rel="noopener noreferrer" className="text-white hover:underline">
                            {attr.photographer}
                          </a>
                          {src === 'unsplash' && (
                            <>
                              {' '}on{' '}
                              <a href="https://unsplash.com/?utm_source=wanderloop&utm_medium=referral" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">
                                Unsplash
                              </a>
                            </>
                          )}
                          {src === 'wikimedia' && ' via Wikimedia Commons'}
                        </span>
                      </div>
                    );
                  }
                  if (src === 'unsplash') {
                    return (
                      <div className="absolute bottom-1 left-2 z-20 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
                        <span className="text-[7px] text-white/80 bg-black/40 px-1.5 py-0.5 rounded-full">
                          Photo via{' '}
                          <a href="https://unsplash.com/?utm_source=wanderloop&utm_medium=referral" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">
                            Unsplash
                          </a>
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Scrollable thumbnail strip — bottom right */}
                {walk.photos.length > 1 && (
                  <div
                    className="absolute bottom-4 right-4 z-20 bg-black/50 backdrop-blur-sm rounded-lg p-1.5 max-w-[340px]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide"
                      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                      ref={(el) => {
                        // Auto-scroll to keep active thumbnail visible
                        if (el) {
                          const active = el.children[heroPhotoIndex];
                          if (active) active.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
                        }
                      }}
                    >
                      {walk.photos.map((photo, i) => {
                        const attr = walk.unsplashAttributions?.[i];
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setHeroPhotoIndex(i);
                            }}
                            className={`flex-shrink-0 rounded overflow-hidden ${
                              i === heroPhotoIndex
                                ? 'scale-105 opacity-100'
                                : 'opacity-[0.55] hover:opacity-80 scale-100'
                            }`}
                            style={{
                              transition: 'opacity 150ms ease, transform 150ms ease, border-color 150ms ease',
                              border: i === heroPhotoIndex ? '2px solid #5B2D8E' : '2px solid transparent',
                            }}
                          >
                            <img
                              src={photo}
                              alt={attr?.alt_text || `${walk.title} - Photo ${i + 1}`}
                              className="w-10 h-10 sm:w-12 sm:h-12 object-cover"
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Floating navigation controls */}
              <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-20">
                <Link to="/walks">
                  <Button variant="ghost" size="sm" className="bg-black/50 text-white hover:bg-black/70">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                </Link>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleShare}
                    className="bg-black/50 text-white hover:bg-black/70"
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleFavoriteClick}
                    className="bg-black/50 text-white hover:bg-black/70"
                  >
                    <Heart className={`h-4 w-4 ${favorite ? 'fill-red-500 text-red-500' : ''}`} />
                  </Button>
                </div>
              </div>

              {/* Title overlay on image */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent text-white z-10 pointer-events-none">
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge
                    className="text-white text-xs font-semibold border-0"
                    style={{ backgroundColor: categoryColor(walk.category) }}
                  >
                    {walk.category}
                  </Badge>
                  <Badge
                    className="text-white text-xs font-semibold border-0"
                    style={{ backgroundColor: difficultyColor(walk.difficulty) }}
                  >
                    {walk.difficulty}{walk.distanceCategory ? ` · ${walk.distanceCategory}` : ''}
                  </Badge>
                </div>
                <h1 className="text-2xl md:text-4xl font-bold mb-3 leading-tight">
                  {walk.title}
                </h1>
                <div className="flex flex-wrap gap-4 text-sm pointer-events-none">
                  {walk.userRatings > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      {walk.userRatings.toFixed(1)}
                      {walk.ratingCount > 0 && <span className="text-white/70">({walk.ratingCount})</span>}
                    </div>
                  )}
                  {walk.duration && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {walk.duration}
                    </div>
                  )}
                  {walk.distance && (
                    <div className="flex items-center gap-1">
                      <Navigation className="h-4 w-4" />
                      {walk.distance}
                    </div>
                  )}
                  {walk.favoriteCount > 0 && (
                    <div className="flex items-center gap-1">
                      <Heart className="h-4 w-4" />
                      {walk.favoriteCount} saves
                    </div>
                  )}
                  {completionCount > 0 && (
                    <div className="flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" />
                      {completionCount} completed
                    </div>
                  )}
                </div>
                {/* I Walked This Button */}
                <div className="mt-3 pointer-events-auto">
                  {userCompleted ? (
                    <Button
                      size="sm"
                      onClick={handleUncomplete}
                      className="bg-amber-500 hover:bg-amber-600 text-white font-bold"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Walked ✓
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => isAuthenticated ? setShowCompletionModal(true) : null}
                      className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Mark as Walked
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Completion Modal */}
            {showCompletionModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl max-w-md w-full p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Log Your Walk</h3>
                    <button onClick={() => setShowCompletionModal(false)}>
                      <X className="h-5 w-5 text-gray-400" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                      <Input
                        type="date"
                        value={completionDate}
                        onChange={(e) => setCompletionDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">How was it? Any tips? (optional)</label>
                      <Textarea
                        placeholder="Share your experience..."
                        value={completionNote}
                        onChange={(e) => setCompletionNote(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <Button
                      onClick={handleCompletionSubmit}
                      disabled={completionSubmitting}
                      className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold"
                    >
                      {completionSubmitting ? 'Saving...' : 'Submit'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Map Section - Right Side */}
            <div className="lg:col-span-1 bg-gray-50 relative">
              <div className="h-[500px] p-4">
                <div className="h-full">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Route Map</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-green-600" />
                        <span>Start</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                        <span>Highlights</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-1 bg-blue-500"></div>
                        <span>Route</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Compact Map */}
                  <div className="h-[300px] rounded-lg overflow-hidden">
                    <WalkRouteMap 
                      walk={walk} 
                      compact={true} 
                      onPOISelect={(poi) => setSelectedPOI(poi)}
                    />
                  </div>

                  {/* Quick Actions */}
                  <div className="mt-4 space-y-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-sm"
                      onClick={() => {
                        const origin = `${walk.startingCoords.lat},${walk.startingCoords.lng}`;
                        const dest = `${walk.endingCoords.lat},${walk.endingCoords.lng}`;
                        const stops = (walk.waypoints || [])
                          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                          .map(wp => {
                            const lat = Number(wp.coords?.lat);
                            const lng = Number(wp.coords?.lng);
                            return (!isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0))
                              ? `${lat},${lng}` : null;
                          })
                          .filter(Boolean);
                        const url = `https://www.google.com/maps/dir/?api=1`
                          + `&origin=${origin}`
                          + `&destination=${dest}`
                          + (stops.length ? `&waypoints=${stops.join('|')}` : '')
                          + `&travelmode=walking`;
                        console.log('[WalkDetailPage] Walking directions URL:', url);
                        window.open(url, '_blank');
                      }}
                    >
                      <Navigation className="mr-2 h-3 w-3" />
                      Get Walking Directions
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-sm"
                      onClick={() => {
                        const start = `${walk.startingCoords.lat},${walk.startingCoords.lng}`;
                        window.open(`https://www.google.com/maps/dir/?api=1&destination=${start}&travelmode=driving`, '_blank');
                      }}
                    >
                      <Car className="mr-2 h-3 w-3" />
                      Driving Directions to Start
                    </Button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Practical Summary */}
      {walk.practicalSummary && (
        <section className="bg-blue-50 py-5">
          <div className="max-w-7xl mx-auto px-4">
            <p className="text-gray-700 leading-relaxed">
              {walk.practicalSummary}
              {walk.walkShape === 'out-and-back' && !walk.practicalSummary?.includes('Out-and-back') && !walk.practicalSummary?.includes('out-and-back') && (
                <span className="text-gray-500"> Out-and-back route. Return the same way or arrange a rideshare from the endpoint.</span>
              )}
              {walk.walkShape === 'point-to-point' && !walk.practicalSummary?.includes('One-way') && !walk.practicalSummary?.includes('point-to-point') && (
                <span className="text-gray-500"> One-way route. Plan for a rideshare return or a second car at the endpoint.</span>
              )}
            </p>
            <span className="block text-[10px] text-gray-300 mt-2">Routes and information are for general guidance only. Conditions may change. Use your own judgment. Wanderloop is not liable for inaccuracies.</span>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Schema-Optimized Walk Info Section */}
            <Card className="bg-gradient-to-r from-green-50 to-blue-50">
              <CardHeader>
                <CardTitle className="text-xl font-bold">Walk Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  {/* Top Row: Distance, Duration, Difficulty */}
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Distance</p>
                      <p className="text-gray-600">{walk.distance || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Duration</p>
                      <p className="text-gray-600">{walk.duration || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Difficulty</p>
                      <p className="text-gray-600">{walk.difficulty}{walk.distanceCategory ? ` · ${walk.distanceCategory}` : ''}</p>
                    </div>
                  </div>

                  {/* Bottom Row: Safety, Route Shape, Dog Friendly */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm flex-shrink-0">🛡️</span>
                    <div>
                      <p className="font-medium">Safety</p>
                      <p className={`text-sm font-medium ${
                        walk.safetyLevel === 'Safe Anytime' ? 'text-green-700' :
                        walk.safetyLevel === 'Safe Until Evening' ? 'text-amber-700' :
                        'text-orange-700'
                      }`}>{walk.safetyLevel}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-blue-600 text-sm flex-shrink-0">
                      {walk.walkShape === 'loop' ? '🔄' : walk.walkShape === 'out-and-back' ? '↔️' : '→'}
                    </span>
                    <div>
                      <p className="font-medium">Route Shape</p>
                      <p className="text-gray-600">
                        {walk.walkShape === 'loop' ? 'Loop' : walk.walkShape === 'out-and-back' ? 'Out & Back' : 'Point-to-Point'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-blue-600 text-sm flex-shrink-0">🐕</span>
                    <div>
                      <p className="font-medium">Dog Friendly</p>
                      <p className="text-gray-600">{walk.accessibility?.dogFriendly ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                  {walk.terrain && (
                    <div className="flex items-center gap-2">
                      <Navigation className="h-4 w-4 text-blue-600" />
                      <div>
                        <p className="font-medium">Terrain</p>
                        <p className="text-gray-600">{walk.terrain}</p>
                      </div>
                    </div>
                  )}
                  {walk.elevationGain && (
                    <div className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-blue-600" />
                      <div>
                        <p className="font-medium">Elevation Gain</p>
                        <p className="text-gray-600">{walk.elevationGain}</p>
                      </div>
                    </div>
                  )}
                  {walk.transitAccess && (
                    <div className="flex items-center gap-2">
                      <Bus className="h-4 w-4 text-blue-600" />
                      <div>
                        <p className="font-medium">Transit Access</p>
                        <p className="text-gray-600">{walk.transitAccess}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Explore Highlights nudge */}
            {(walk.highlights?.length > 0 || walk.culturalPois?.length > 0) && (
              <div className="text-center -mt-1 mb--1">
                <button
                  type="button"
                  onClick={() => document.getElementById('walk-highlights')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-purple-600 hover:text-purple-800 text-sm font-medium transition-colors inline-flex items-center gap-1"
                >
                  Explore Highlights
                  <span className="text-xs">↓</span>
                </button>
              </div>
            )}

            {/* Tags */}
            {walk.seoTags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {walk.seoTags.map((tag, i) => (
                  <button
                    key={i}
                    onClick={() => handleTagClick(tag)}
                    className="inline-flex items-center px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-100 hover:text-blue-800 transition-colors cursor-pointer"
                  >
                    {tag.replace(/^#/, '')}
                  </button>
                ))}
              </div>
            )}

            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-bold">About This Walk{walk.region ? ` in ${walk.region}` : ''}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(walk.description || '').split(/\n\n+/).map((para, i) => (
                  <p key={i} className="text-lg leading-relaxed">{para}</p>
                ))}
              </CardContent>
            </Card>

            {/* Walk Highlights - Unified Section */}
            <div id="walk-highlights">
            <WalkHighlights
              walkId={walk.id}
              walkCoordinates={walk.routeCoordinates}
              regularHighlights={walk.highlights}
              savedCulturalPOIs={walk.culturalPois}
              onPOISelect={(poi) => {
                setSelectedPOI(poi);
              }}
              onTagClick={handleTagClick}
              title="Walk Highlights"
            />
            </div>

            {/* Route Stops Timeline (only in timeline layout) */}
            {isTimeline && walk.waypoints?.length > 0 && (
              <div id="route-timeline">
                <RouteTimeline waypoints={walk.waypoints} walk={walk} />
              </div>
            )}

            {/* Gear for This Walk */}
            {(linkedGear.length > 0 || walk.gearNotes) && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl font-bold">Gear for This Walk</CardTitle>
                    <Link to="/gear" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 border border-[#F5A623] rounded-full text-xs font-bold text-[#F5A623] hover:bg-amber-50 transition-colors">
                      See all gear →
                    </Link>
                  </div>
                  {walk.gearNotes && (
                    <p className="text-sm text-gray-600 leading-relaxed max-w-prose">{walk.gearNotes}</p>
                  )}
                </CardHeader>
                <CardContent className={walk.gearNotes ? 'pt-4' : ''}>
                  {linkedGear.length > 0 && (
                    <div className={`grid gap-3 ${linkedGear.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
                      {linkedGear.map(g => (
                        <Link
                          key={g.slug}
                          to={`/gear/${g.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 border rounded-lg hover:bg-amber-50 hover:border-amber-200 transition-colors"
                        >
                          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden border">
                            {g.images?.[0] ? (
                              <img src={g.images[0]} alt={g.name} className="w-full h-full object-contain p-1" />
                            ) : (
                              <span className="text-xs text-gray-300">{g.brand}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900">{g.name}</p>
                            {(() => {
                              const editorial = walk.gearEditorialNotes?.[g.slug];
                              const note = editorial || g.bens_take;
                              if (!note) return null;
                              return <p className="text-xs text-gray-600 italic mt-0.5 line-clamp-2">{note}</p>;
                            })()}
                            <p className="text-xs text-gray-500 mt-0.5">
                              {g.brand} · ${g.price}
                              {g.rating > 0 && (
                                <span className="inline-flex items-center gap-0.5 ml-1.5">
                                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400 inline" />
                                  {g.rating}
                                </span>
                              )}
                            </p>
                          </div>
                          <span className="text-xs text-[#F5A623] font-medium flex-shrink-0">View & Buy →</span>
                        </Link>
                      ))}
                    </div>
                  )}
                  {linkedGear.length > 0 && (
                    <p className="text-xs text-gray-400 italic mt-3">
                      Affiliate links — Wanderloop may earn a commission on purchases.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Walk Personality Section - Enhanced and More Prominent */}
            {walk.funCategories && Object.values(walk.funCategories).some(Boolean) && (
              <Card className="bg-gradient-to-r from-purple-50 via-pink-50 to-yellow-50 border-none shadow-lg">
                <CardHeader className="text-center pb-6">
                  <CardTitle className="text-xl font-bold">Walk Personality</CardTitle>
                  <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    What gives this walk its unique soul and character
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[
                      walk.funCategories.themeSong && {
                        label: 'Theme Song', emoji: '🎵',
                        gradient: 'from-purple-500 to-pink-500',
                        content: walk.funCategories.themeSong,
                        link: `https://open.spotify.com/search/${encodeURIComponent(walk.funCategories.themeSong.split('|')[0].trim())}`,
                        linkLabel: 'Listen on Spotify →', linkColor: 'text-purple-500',
                        hasSplit: true,
                      },
                      walk.funCategories.movieThatMatches && {
                        label: 'Movie that Matches', emoji: '🎬',
                        gradient: 'from-red-500 to-orange-500',
                        content: walk.funCategories.movieThatMatches,
                        link: `https://www.imdb.com/find/?q=${encodeURIComponent(walk.funCategories.movieThatMatches.split('|')[0].trim())}`,
                        linkLabel: 'Find on IMDb →', linkColor: 'text-red-500',
                        hasSplit: true,
                      },
                      walk.funCategories.vibeIn3Words && {
                        label: 'Vibe in 3 Words', emoji: '✨',
                        gradient: 'from-blue-500 to-cyan-500',
                        content: (() => {
                          const raw = Array.isArray(walk.funCategories.vibeIn3Words) ? walk.funCategories.vibeIn3Words.join(', ') : walk.funCategories.vibeIn3Words;
                          return raw.replace(/\b\w/g, c => c.toUpperCase());
                        })(),
                        hasSplit: true,
                      },
                      walk.funCategories.instagramMoment && {
                        label: 'Instagram Moment', emoji: '📸',
                        gradient: 'from-pink-500 to-rose-500',
                        content: walk.funCategories.instagramMoment,
                      },
                      walk.funCategories.perfectSnack && {
                        label: 'Perfect Snack', emoji: '🍿',
                        gradient: 'from-orange-500 to-yellow-500',
                        content: walk.funCategories.perfectSnack,
                      },
                      walk.funCategories.walkingChallenge && {
                        label: 'Walking Challenge', emoji: '🎯',
                        gradient: 'from-green-500 to-teal-500',
                        content: walk.funCategories.walkingChallenge,
                      },
                    ].filter(Boolean).map((card, i) => {
                      const Wrapper = card.link ? 'a' : 'div';
                      const wrapperProps = card.link ? { href: card.link, target: '_blank', rel: 'noopener noreferrer' } : {};
                      const parts = card.hasSplit ? card.content.split('|').map(s => s.trim()) : [card.content];
                      return (
                        <Wrapper key={i} {...wrapperProps} className="block h-full">
                          <Card className={`text-center hover:shadow-lg transition-all bg-white/90 backdrop-blur-sm border-0 hover:scale-105 h-full ${!card.hasSplit ? 'min-h-[220px]' : ''} ${card.link ? 'cursor-pointer' : ''}`}>
                            <CardContent className="p-5 h-full flex flex-col items-center justify-between">
                              {/* Zone 1: Icon — fixed height */}
                              <div className="h-[60px] flex items-center justify-center">
                                <div className={`w-12 h-12 bg-gradient-to-br ${card.gradient} rounded-full flex items-center justify-center`}>
                                  <span className="text-white text-lg">{card.emoji}</span>
                                </div>
                              </div>
                              {/* Zone 2: Label — fixed height */}
                              <div className="h-[28px] flex items-center justify-center">
                                <h3 className="font-semibold text-xs uppercase tracking-wider text-gray-400">{card.label}</h3>
                              </div>
                              {/* Zone 3: Content — flex-grows to fill */}
                              <div className="flex-1 flex flex-col items-center justify-center py-2 w-full">
                                <p className="font-bold text-[15px] text-gray-900 leading-snug">{parts[0]}</p>
                                {parts[1] && <p className="text-xs text-gray-500 mt-1.5 leading-relaxed max-w-[220px]">{parts[1]}</p>}
                              </div>
                              {/* Zone 4: Footer link — fixed height, pinned bottom */}
                              <div className="h-[32px] flex items-center justify-center w-full">
                                {card.linkLabel ? (
                                  <p className={`text-xs ${card.linkColor} pt-2 border-t border-gray-100 w-full`}>{card.linkLabel}</p>
                                ) : (
                                  <div />
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        </Wrapper>
                      );
                    })}
                  </div>

                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info */}
            <Card>
              <CardHeader>
                <CardTitle>Walk Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {walk.startingPoint && walk.startingPoint !== 'Unknown' && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="font-medium">Starting Point</p>
                      <p className="text-sm text-gray-600">{walk.startingPoint}</p>
                    </div>
                  </div>
                )}
                {walk.endingPoint && walk.endingPoint !== 'Unknown' && (
                  <div className="flex items-center gap-3">
                    <Navigation className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="font-medium">Ending Point</p>
                      <p className="text-sm text-gray-600">{walk.endingPoint}</p>
                    </div>
                  </div>
                )}
                {walk.transitAccess && (
                  <div className="flex items-center gap-3">
                    <Bus className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="font-medium">Transit Access</p>
                      <p className="text-sm text-gray-600">{walk.transitAccess}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {walk.waypoints && walk.waypoints.length >= 2 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Navigation className="h-5 w-5" />
                    Route Stops
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isTimeline ? (
                    <RouteTimelineIndex
                      waypoints={walk.waypoints}
                      onStopClick={(i) => {
                        const el = document.querySelector(`[data-stop-index="${i}"]`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                    />
                  ) : (
                  <ol className="space-y-2">
                    {walk.waypoints
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                      .map((wp, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-3 p-2 -mx-2 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
                          onClick={() => {
                            // Match waypoint to cultural POI for place_id and description
                            const wpName = (wp.name || '').toLowerCase();
                            const matchedPOI = (walk.culturalPois || []).find(poi =>
                              (wp.place_id && poi.place_id && wp.place_id === poi.place_id) ||
                              (wpName && (poi.name || poi.title || '').toLowerCase() === wpName)
                            );
                            setSelectedPOI({
                              name: wp.name || `Stop ${i + 1}`,
                              address: wp.address || '',
                              description: matchedPOI?.description || wp.description || wp.address || '',
                              category: matchedPOI ? (matchedPOI.category || 'waypoint') : 'waypoint',
                              place_id: wp.place_id || matchedPOI?.place_id || null,
                              coordinates: wp.coords || null,
                              type: matchedPOI ? 'highlight' : 'waypoint',
                              user_ratings: matchedPOI?.user_ratings || 0,
                            });
                          }}
                        >
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold flex-shrink-0 mt-0.5 ${
                            i === 0 || i === walk.waypoints.length - 1 ? 'bg-purple-700' : 'bg-blue-600'
                          }`}>
                            {i + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm text-blue-600">{wp.name || `Stop ${i + 1}`}</p>
                            {wp.address && <p className="text-xs text-gray-500 truncate">{wp.address}</p>}
                            {wp.description && <p className="text-xs text-gray-600 mt-0.5">{wp.description}</p>}
                          </div>
                        </li>
                      ))}
                  </ol>
                  )}
                </CardContent>
              </Card>
            )}

            {walk.bestTimes?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sun className="h-5 w-5" />
                    Best Times to Visit
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {walk.bestTimes.map(key => {
                      const labels = {
                        early_morning: '🌅 Early Morning', morning: '☀️ Morning', midday: '🌤 Midday',
                        late_afternoon: '🌇 Late Afternoon', sunset: '🌆 Sunset', evening: '🌙 Evening',
                        weekdays: '📅 Weekdays', weekends: '🎉 Weekends',
                        spring: '🌸 Spring', summer: '🌊 Summer', fall: '🍂 Fall', winter: '🧥 Winter',
                        avoid_summer_midday: '⚠️ Avoid Summer Midday', avoid_after_rain: '🌧️ Avoid After Rain',
                      };
                      return (
                        <button
                          key={key}
                          onClick={() => navigate(`/walks/${walk.country || 'us'}/${walk.city || 'los-angeles'}?time=${encodeURIComponent(key)}`)}
                          className="inline-flex items-center px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-100 hover:text-blue-800 transition-colors cursor-pointer"
                        >
                          {labels[key] || key}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Weather */}
            <WeatherWidget lat={walk.startingCoords?.lat} lng={walk.startingCoords?.lng} />

            {/* Accessibility — Option C: hide whole card when ALL fields are null
                so we don't broadcast wrong "No"s on walks with no data. */}
            {(() => {
              const a = walk.accessibility;
              const fields = [a.strollerFriendly, a.wheelchairAccessible, a.dogFriendly, a.parkingAvailable, a.restrooms, a.shadeCoverage];
              const hasAnyData = fields.some(v => v !== null);
              if (!hasAnyData) return null;
              const yesNo = (v) =>
                v === true  ? <Badge variant="default">Yes</Badge> :
                v === false ? <Badge variant="secondary">No</Badge> :
                              <Badge variant="outline" className="italic text-gray-500">Unknown</Badge>;
              const shadeColor = {
                Limited:   'bg-amber-100 text-amber-800',
                Moderate:  'bg-yellow-100 text-yellow-800',
                Good:      'bg-emerald-100 text-emerald-800',
                Excellent: 'bg-green-200 text-green-900',
              };
              return (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Accessibility className="h-5 w-5" />
                      Accessibility
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between"><span className="text-sm">Stroller Friendly</span>{yesNo(a.strollerFriendly)}</div>
                      <div className="flex items-center justify-between"><span className="text-sm">Wheelchair Accessible</span>{yesNo(a.wheelchairAccessible)}</div>
                      <div className="flex items-center justify-between"><span className="text-sm">Dog Friendly</span>{yesNo(a.dogFriendly)}</div>
                      <div className="flex items-center justify-between"><span className="text-sm">Parking Available</span>{yesNo(a.parkingAvailable)}</div>
                      <div className="flex items-center justify-between"><span className="text-sm">Restrooms</span>{yesNo(a.restrooms)}</div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Shade Coverage</span>
                        {a.shadeCoverage
                          ? <Badge className={shadeColor[a.shadeCoverage] || 'bg-gray-100 text-gray-700'}>{a.shadeCoverage}</Badge>
                          : <Badge variant="outline" className="italic text-gray-500">Unknown</Badge>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Community Feedback */}
            <button
              onClick={() => setShowFeedbackModal(true)}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 border-2 border-purple-300 text-purple-700 text-sm font-medium rounded-lg hover:bg-purple-50 hover:border-purple-400 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              Know this walk? Suggest an improvement
            </button>

          </div>
        </div>

        {/* Related Walks Section - At Bottom */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4 text-gray-900">More Walks You'll Love</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Discover similar adventures and explore more of what Los Angeles has to offer
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedWalks.map((relatedWalk) => (
                <WalkCard 
                  key={`related-${relatedWalk.id}-${refreshKey}`} 
                  walk={relatedWalk} 
                  onFavoriteToggle={handleFavoriteClick}
                />
              ))}
            </div>
          </div>
        </section>
      </section>

      {/* Floating Actions */}
      <FloatingActions />
      
      {/* POI Detail Modal */}
      <POIDetailModal
        poi={selectedPOI}
        isOpen={!!selectedPOI}
        onClose={() => setSelectedPOI(null)}
        onTagClick={handleTagClick}
        waypoints={walk?.waypoints || []}
        totalStops={(walk?.highlights?.length || 0) + (walk?.culturalPois?.length || 0)}
      />

      <ShareModal
        walk={walk}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />

      <WalkFeedbackModal
        walk={walk}
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />

      {/* Photo Gallery Modal */}
      {showPhotoGallery && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl h-full max-h-[90vh] flex flex-col">
            {/* Gallery Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">Photos of {walk.title}</h3>
                <p className="text-sm text-gray-600">{walk.title}</p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowPhotoGallery(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Photo Grid */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {walk.photos.map((photo, index) => (
                  <div 
                    key={index}
                    className="relative overflow-hidden rounded-lg cursor-pointer group"
                    onClick={() => {
                      setShowPhotoGallery(false);
                      openPhotoSlideshow(index);
                    }}
                  >
                    <img 
                      src={photo} 
                      alt={`${walk.title} - Photo ${index + 1}`}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo Slideshow Modal */}
      {showPhotoSlideshow && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          {/* Close button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowPhotoSlideshow(false)}
            className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
          >
            <X className="h-6 w-6" />
          </Button>

          {/* Previous button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={prevPhoto}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
          >
            <ChevronLeft className="h-8 w-8" />
          </Button>

          {/* Next button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={nextPhoto}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
          >
            <ChevronRight className="h-8 w-8" />
          </Button>

          {/* Main photo */}
          <div className="relative w-full h-full flex items-center justify-center p-8">
            <img 
              src={walk.photos[currentPhotoIndex]} 
              alt={walk.unsplashAttributions?.[currentPhotoIndex]?.alt_text || `${walk.title} - Photo ${currentPhotoIndex + 1}`}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {/* Photo counter + attribution */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
            {(() => {
              const attr = walk.unsplashAttributions?.[currentPhotoIndex];
              const src = attr?.source || ((walk.photos[currentPhotoIndex] || '').includes('unsplash.com') ? 'unsplash' : 'manual');
              if (attr?.photographer) {
                return (
                  <span className="text-[11px] text-white/80 bg-black/50 px-3 py-1 rounded-full">
                    {src === 'wikimedia' ? 'Image' : 'Photo'} by{' '}
                    <a href={`${attr.url}${src === 'unsplash' ? '?utm_source=wanderloop&utm_medium=referral' : ''}`} target="_blank" rel="noopener noreferrer" className="text-white hover:underline">{attr.photographer}</a>
                    {src === 'unsplash' && (
                      <>
                        {' '}on{' '}
                        <a href="https://unsplash.com/?utm_source=wanderloop&utm_medium=referral" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">Unsplash</a>
                      </>
                    )}
                    {src === 'wikimedia' && ' via Wikimedia Commons'}
                  </span>
                );
              }
              if (src === 'unsplash') {
                return (
                  <span className="text-[11px] text-white/80 bg-black/50 px-3 py-1 rounded-full">
                    Photo via <a href="https://unsplash.com/?utm_source=wanderloop&utm_medium=referral" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">Unsplash</a>
                  </span>
                );
              }
              return null;
            })()}
            <div className="bg-black/70 text-white px-4 py-2 rounded-lg">
              <span className="text-sm">
                {currentPhotoIndex + 1} of {walk.photos.length}
              </span>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default WalkDetailPage;